import React, { useMemo } from 'react';
import { Padding } from '@extensions/AttributePanel/components/attributes/Padding';
import {
	EditGridTabField,
	InputWithUnitField,
	RadioGroupField,
	SwitchField,
	TextField,
} from '@extensions/components/Form';
import { Align } from '@extensions/AttributePanel/components/attributes/Align';
import { Color } from '@extensions/AttributePanel/components/attributes/Color';
import { ContainerBackgroundColor } from '@extensions/AttributePanel/components/attributes/ContainerBackgroundColor';
import { FontFamily } from '@extensions/AttributePanel/components/attributes/FontFamily';
import { FontSize } from '@extensions/AttributePanel/components/attributes/FontSize';
import { FontStyle } from '@extensions/AttributePanel/components/attributes/FontStyle';
import { FontWeight } from '@extensions/AttributePanel/components/attributes/FontWeight';
import { MergeTags } from '@extensions/AttributePanel/components/attributes/MergeTags';

import { Collapse, Grid, Space } from '@arco-design/web-react';
import { TextDecoration } from '@extensions/AttributePanel/components/attributes/TextDecoration';
import { LineHeight } from '@extensions/AttributePanel/components/attributes/LineHeight';
import { useBlock, useFocusIdx } from '@bay-email/editor';
import { ISocial, t } from '@bay-email/core';
import { ClassName } from '../../attributes/ClassName';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import { pixelAdapter } from '../../adapter';
import { socialTargetAdapter } from './socialTarget.adapter';
import { useField } from 'react-final-form';
import type { MergeTagContext } from '@extensions/utils/getMergeTagsForContext';

const options = [
	{
		value: 'vertical',
		get label() {
			return t('Vertical');
		},
	},
	{
		value: 'horizontal',
		get label() {
			return t('Horizontal');
		},
	},
];

type SocialNetworkKey =
	| 'facebook'
	| 'instagram'
	| 'social_x'
	| 'linkedin'
	| 'linkedin-business'
	| 'youtube'
	| 'tiktok'
	| 'pinterest'
	| 'yelp';

type LegacySocialNetworkKey = 'twitter' | 'x';

const DEFAULT_SOCIAL_COLOR = '#333333';
const DEFAULT_SOCIAL_ICON_SIZE = '20';
const WEBSITE_ICON_PATH =
	'M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m7.5-6.923c-.67.204-1.335.82-1.887 1.855A8 8 0 0 0 5.145 4H7.5zM4.09 4a9.3 9.3 0 0 1 .64-1.539 7 7 0 0 1 .597-.933A7.03 7.03 0 0 0 2.255 4zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a7 7 0 0 0-.656 2.5zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5zM8.5 5v2.5h2.99a12.5 12.5 0 0 0-.337-2.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5zM5.145 12q.208.58.468 1.068c.552 1.035 1.218 1.65 1.887 1.855V12zm.182 2.472a7 7 0 0 1-.597-.933A9.3 9.3 0 0 1 4.09 12H2.255a7 7 0 0 0 3.072 2.472M3.82 11a13.7 13.7 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5zm6.853 3.472A7 7 0 0 0 13.745 12H11.91a9.3 9.3 0 0 1-.64 1.539 7 7 0 0 1-.597.933M8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855q.26-.487.468-1.068zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.7 13.7 0 0 1-.312 2.5m2.802-3.5a7 7 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7 7 0 0 0-3.072-2.472c.218.284.418.598.597.933M10.855 4a8 8 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4z';

function normalizeSvgSize(size?: string) {
	const match = size?.trim().match(/\d+(?:\.\d+)?/);
	return match?.[0] || DEFAULT_SOCIAL_ICON_SIZE;
}

function getWebsiteIconSrc({
	color = DEFAULT_SOCIAL_COLOR,
	size = DEFAULT_SOCIAL_ICON_SIZE,
}: {
	color?: string;
	size?: string;
} = {}) {
	const normalizedSize = normalizeSvgSize(size);
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${normalizedSize}" height="${normalizedSize}" fill="${color}" viewBox="0 0 16 16"><path d="${WEBSITE_ICON_PATH}"/></svg>`;
	return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const SOCIAL_DUPLICATE_TOOLTIP = "You've already added this social network.";
const SOCIAL_TOOLTIP_PLACEMENT = 'topRight';
const INSTAGRAM_ICON_SRC = 'https://bay.cdn.bkat.io/smc/instagram-icon.png';

const socialPlatformOptions: Array<{
	key: SocialNetworkKey;
	label: string;
	icon: string;
	mergeTagContext: MergeTagContext;
	data: ISocial['data']['value']['elements'][0];
	tooltip: string;
}> = [
	{
		key: 'facebook',
		label: 'Facebook',
		icon: 'https://bay.cdn.bkat.io/smc/facebook-icon.png',
		mergeTagContext: 'social-facebook-link',
		data: {
			name: 'facebook',
			href: '#',
			target: '_blank',
			src: 'https://bay.cdn.bkat.io/smc/facebook-icon.png',
			content: '',
		},
		tooltip: SOCIAL_DUPLICATE_TOOLTIP,
	},
	{
		key: 'instagram',
		label: 'Instagram',
		icon: INSTAGRAM_ICON_SRC,
		mergeTagContext: 'social-instagram-link',
		data: {
			name: 'instagram',
			href: '#',
			target: '_blank',
			src: INSTAGRAM_ICON_SRC,
			content: '',
		},
		tooltip: SOCIAL_DUPLICATE_TOOLTIP,
	},
	{
		key: 'social_x',
		label: 'X',
		icon: 'https://bay.cdn.bkat.io/smc/twitter-logo-gray.png',
		mergeTagContext: 'social-x-link',
		data: {
			name: 'social_x',
			href: '#',
			target: '_blank',
			src: 'https://bay.cdn.bkat.io/smc/twitter-logo-gray.png',
			content: '',
		},
		tooltip: SOCIAL_DUPLICATE_TOOLTIP,
	},
	{
		key: 'linkedin',
		label: 'LinkedIn',
		icon: 'https://bay.cdn.bkat.io/smc/linkedin-icon.png',
		mergeTagContext: 'social-linkedin-link',
		data: {
			name: 'linkedin',
			href: '#',
			target: '_blank',
			src: 'https://bay.cdn.bkat.io/smc/linkedin-icon.png',
			content: '',
		},
		tooltip: SOCIAL_DUPLICATE_TOOLTIP,
	},
	{
		key: 'linkedin-business',
		label: 'LinkedIn Business',
		icon: 'https://bay.cdn.bkat.io/smc/linkedin_business-icon.png',
		mergeTagContext: 'social-linkedin-business-link',
		data: {
			name: 'linkedin-business',
			href: '#',
			target: '_blank',
			src: 'https://bay.cdn.bkat.io/smc/linkedin_business-icon.png',
			content: '',
		},
		tooltip: SOCIAL_DUPLICATE_TOOLTIP,
	},
	{
		key: 'youtube',
		label: 'YouTube',
		icon: 'https://cdn-icons-png.flaticon.com/128/174/174883.png',
		mergeTagContext: 'social-youtube-link',
		data: {
			name: 'youtube',
			href: '#',
			target: '_blank',
			src: 'https://cdn-icons-png.flaticon.com/128/174/174883.png',
			content: '',
		},
		tooltip: SOCIAL_DUPLICATE_TOOLTIP,
	},
	{
		key: 'tiktok',
		label: 'TikTok',
		icon: 'https://cdn-icons-png.flaticon.com/128/3046/3046126.png',
		mergeTagContext: 'social-tiktok-link',
		data: {
			name: 'tiktok',
			href: '#',
			target: '_blank',
			src: 'https://cdn-icons-png.flaticon.com/128/3046/3046126.png',
			content: '',
		},
		tooltip: SOCIAL_DUPLICATE_TOOLTIP,
	},
	{
		key: 'pinterest',
		label: 'Pinterest',
		icon: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M9.99996%206.86646e-05C4.47716%206.86646e-05%200%204.47723%200%2010C0%2014.2365%202.63582%2017.8581%206.35572%2019.3152C6.26834%2018.524%206.18931%2017.3072%206.39039%2016.4435C6.57213%2015.663%207.56301%2011.4728%207.56301%2011.4728C7.56301%2011.4728%207.26389%2010.8738%207.26389%209.98814C7.26389%208.59765%208.06984%207.55955%209.07335%207.55955C9.92651%207.55955%2010.3386%208.20008%2010.3386%208.96816C10.3386%209.82617%209.79232%2011.109%209.51042%2012.2977C9.27483%2013.293%2010.0095%2014.1047%2010.9912%2014.1047C12.7686%2014.1047%2014.1348%2012.2307%2014.1348%209.52549C14.1348%207.13116%2012.4144%205.45713%209.95774%205.45713C7.1124%205.45713%205.4423%207.59127%205.4423%209.79682C5.4423%2010.6563%205.77339%2011.5779%206.18653%2012.0789C6.26825%2012.1779%206.28022%2012.2647%206.25588%2012.3657C6.17997%2012.6816%206.01127%2013.3608%205.97815%2013.4998C5.93455%2013.6829%205.83323%2013.7217%205.64371%2013.6335C4.39478%2013.0522%203.61391%2011.2262%203.61391%209.75952C3.61391%206.60507%205.90586%203.70808%2010.2213%203.70808C13.6903%203.70808%2016.3861%206.18004%2016.3861%209.4836C16.3861%2012.93%2014.2131%2015.7036%2011.197%2015.7036C10.1837%2015.7036%209.23106%2015.1772%208.90497%2014.5553C8.90497%2014.5553%208.40355%2016.4646%208.28199%2016.9325C8.05623%2017.8011%207.44677%2018.8898%207.03912%2019.5541C7.97475%2019.8438%208.96908%2020%209.99996%2020C15.5228%2020%2020%2015.5228%2020%2010C20%204.47723%2015.5228%206.86646e-05%209.99996%206.86646e-05Z%22%20fill%3D%22%23E60022%22/%3E%3C/svg%3E',
		mergeTagContext: 'social-pinterest-link',
		data: {
			name: 'pinterest',
			href: '#',
			target: '_blank',
			src: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M9.99996%206.86646e-05C4.47716%206.86646e-05%200%204.47723%200%2010C0%2014.2365%202.63582%2017.8581%206.35572%2019.3152C6.26834%2018.524%206.18931%2017.3072%206.39039%2016.4435C6.57213%2015.663%207.56301%2011.4728%207.56301%2011.4728C7.56301%2011.4728%207.26389%2010.8738%207.26389%209.98814C7.26389%208.59765%208.06984%207.55955%209.07335%207.55955C9.92651%207.55955%2010.3386%208.20008%2010.3386%208.96816C10.3386%209.82617%209.79232%2011.109%209.51042%2012.2977C9.27483%2013.293%2010.0095%2014.1047%2010.9912%2014.1047C12.7686%2014.1047%2014.1348%2012.2307%2014.1348%209.52549C14.1348%207.13116%2012.4144%205.45713%209.95774%205.45713C7.1124%205.45713%205.4423%207.59127%205.4423%209.79682C5.4423%2010.6563%205.77339%2011.5779%206.18653%2012.0789C6.26825%2012.1779%206.28022%2012.2647%206.25588%2012.3657C6.17997%2012.6816%206.01127%2013.3608%205.97815%2013.4998C5.93455%2013.6829%205.83323%2013.7217%205.64371%2013.6335C4.39478%2013.0522%203.61391%2011.2262%203.61391%209.75952C3.61391%206.60507%205.90586%203.70808%2010.2213%203.70808C13.6903%203.70808%2016.3861%206.18004%2016.3861%209.4836C16.3861%2012.93%2014.2131%2015.7036%2011.197%2015.7036C10.1837%2015.7036%209.23106%2015.1772%208.90497%2014.5553C8.90497%2014.5553%208.40355%2016.4646%208.28199%2016.9325C8.05623%2017.8011%207.44677%2018.8898%207.03912%2019.5541C7.97475%2019.8438%208.96908%2020%209.99996%2020C15.5228%2020%2020%2015.5228%2020%2010C20%204.47723%2015.5228%206.86646e-05%209.99996%206.86646e-05Z%22%20fill%3D%22%23E60022%22/%3E%3C/svg%3E',
			content: '',
		},
		tooltip: SOCIAL_DUPLICATE_TOOLTIP,
	},
	{
		key: 'yelp',
		label: 'Yelp',
		icon: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M5.23438%2012.6189L6.15531%2012.4061C6.17468%2012.4016%206.20667%2012.3943%206.24575%2012.3817C6.49986%2012.3133%206.71761%2012.149%206.85314%2011.9232C6.98869%2011.6975%207.03144%2011.4279%206.9724%2011.1715C6.97111%2011.1658%206.96989%2011.1605%206.96859%2011.1549C6.93834%2011.0319%206.88535%2010.9157%206.81236%2010.8124C6.70915%2010.6814%206.58143%2010.5719%206.43635%2010.4899C6.26556%2010.3925%206.08681%2010.3096%205.90201%2010.2424L4.89226%209.87383C4.32559%209.66318%203.75896%209.45779%203.187%209.25749C2.81608%209.12549%202.50182%209.00964%202.22875%208.92518C2.1772%208.90931%202.12035%208.89343%202.07411%208.87746C1.74399%208.77629%201.51204%208.73434%201.31575%208.73296C1.18464%208.72814%201.05404%208.75196%200.933023%208.80275C0.806914%208.85714%200.693652%208.93752%200.600647%209.03867C0.554384%209.09129%200.510887%209.14626%200.470334%209.20333C0.43228%209.26157%200.397381%209.32171%200.365789%209.38367C0.331002%209.45017%200.301105%209.51903%200.276342%209.58979C0.0889311%2010.1438%20-0.00442136%2010.7252%200.000160845%2011.31C0.00353922%2011.8386%200.0177376%2012.5171%200.308305%2012.977C0.378321%2013.0951%200.472281%2013.1971%200.584127%2013.2766C0.791129%2013.4195%201.00031%2013.4384%201.21793%2013.454C1.54315%2013.4774%201.85822%2013.3974%202.17231%2013.3249L5.23181%2012.618L5.23438%2012.6189Z%22%20fill%3D%22%23FF1A1A%22/%3E%3Cpath%20d%3D%22M15.5088%207.7295C15.2574%207.20179%2014.9194%206.72002%2014.5088%206.3042C14.4557%206.25142%2014.3988%206.20257%2014.3386%206.15808C14.283%206.11635%2014.2252%206.07757%2014.1655%206.04189C14.1042%206.00824%2014.041%205.97791%2013.9764%205.951C13.8487%205.90087%2013.7118%205.87827%2013.5748%205.88467C13.4437%205.89212%2013.3158%205.92803%2013.2%205.98991C13.0243%206.07722%2012.8343%206.21755%2012.5815%206.45253C12.5466%206.48721%2012.5027%206.52635%2012.4633%206.56325C12.2548%206.75932%2012.0228%207.00104%2011.7467%207.28253C11.3203%207.7135%2010.9002%208.14684%2010.4828%208.58491L9.73604%209.35936C9.59933%209.50088%209.47477%209.65374%209.36382%209.81631C9.26929%209.95366%209.20234%2010.1082%209.16687%2010.2712C9.14637%2010.3961%209.14936%2010.5238%209.17576%2010.6477C9.17703%2010.6533%209.1783%2010.6586%209.17957%2010.6643C9.23863%2010.9208%209.39476%2011.1443%209.6153%2011.2878C9.83574%2011.4313%2010.1033%2011.4835%2010.3616%2011.4335C10.4026%2011.4276%2010.4343%2011.4205%2010.4536%2011.4158L14.4355%2010.4958C14.7493%2010.4232%2015.0677%2010.3568%2015.3496%2010.1932C15.5387%2010.0835%2015.7186%209.9748%2015.842%209.75553C15.9078%209.63506%2015.9477%209.50206%2015.9591%209.36526C16.0201%208.82258%2015.7368%208.20628%2015.5088%207.7295Z%22%20fill%3D%22%23FF1A1A%22/%3E%3Cpath%20d%3D%22M8.38218%209.4039C8.67073%209.04139%208.67002%208.50113%208.69583%208.05941C8.78268%206.58357%208.87412%205.10754%208.94661%203.63089C8.97428%203.07158%209.03451%202.51985%209.00122%201.95629C8.97382%201.49137%208.97047%200.957582%208.67693%200.576045C8.15878%20-0.0967628%207.05391%20-0.0414661%206.30005%200.0628987C6.06912%200.0949533%205.83778%200.138051%205.60832%200.19314C5.37884%200.24823%205.15172%200.308397%204.92951%200.379562C4.20614%200.61651%203.1892%201.05145%203.017%201.88495C2.91992%202.35615%203.14996%202.83816%203.32842%203.26817C3.54473%203.78944%203.84019%204.25899%204.10948%204.74968C4.82142%206.0451%205.54658%207.33244%206.26969%208.62114C6.48561%209.00583%206.721%209.49308%207.13879%209.69239C7.16641%209.70446%207.19461%209.71516%207.2233%209.72432C7.41065%209.79527%207.61486%209.80896%207.81006%209.76361C7.82184%209.76088%207.83335%209.75825%207.84486%209.75553C8.0251%209.70654%208.1883%209.60857%208.31634%209.4724C8.33946%209.45072%208.36144%209.42785%208.38218%209.4039Z%22%20fill%3D%22%23FF1A1A%22/%3E%3Cpath%20d%3D%22M8.03672%2013.355C7.92409%2013.1966%207.76426%2013.0778%207.58007%2013.0158C7.39589%2012.9537%207.19678%2012.9515%207.01119%2013.0095C6.9676%2013.0239%206.92512%2013.0416%206.88408%2013.0622C6.82061%2013.0945%206.76006%2013.1323%206.70309%2013.1751C6.53714%2013.298%206.3974%2013.4578%206.27017%2013.618C6.23789%2013.6588%206.20843%2013.7132%206.17004%2013.7482L5.52957%2014.6292C5.16654%2015.1227%204.80853%2015.6175%204.45352%2016.1202C4.22189%2016.4447%204.02153%2016.7187%203.86326%2016.9612C3.83327%2017.0069%203.80212%2017.0579%203.77363%2017.0985C3.58395%2017.392%203.47652%2017.6062%203.42138%2017.7969C3.37996%2017.9238%203.36686%2018.058%203.38298%2018.1904C3.40061%2018.3283%203.44703%2018.4608%203.51923%2018.5796C3.55761%2018.6391%203.59898%2018.6967%203.64318%2018.752C3.68911%2018.8054%203.73789%2018.8561%203.7893%2018.9041C3.84421%2018.9564%203.90293%2019.0047%203.96499%2019.0484C4.40706%2019.3559%204.89101%2019.5769%205.40007%2019.7477C5.82368%2019.8883%206.26401%2019.9725%206.70967%2019.998C6.78549%2020.0019%206.86148%2020.0001%206.93707%2019.9931C7.00712%2019.987%207.07681%2019.9772%207.14585%2019.9638C7.21484%2019.9477%207.28292%2019.9278%207.3498%2019.9045C7.47995%2019.8558%207.59845%2019.7804%207.69766%2019.6831C7.79158%2019.589%207.86386%2019.4756%207.90946%2019.3507C7.98349%2019.1662%208.03218%2018.9318%208.0642%2018.584C8.06701%2018.5346%208.07406%2018.4752%208.07898%2018.4208C8.10424%2018.1322%208.11582%2017.7932%208.13439%2017.3946C8.16571%2016.7815%208.19028%2016.1712%208.2097%2015.5591C8.2097%2015.5591%208.25095%2014.4714%208.25082%2014.4707C8.26027%2014.2201%208.25254%2013.9423%208.183%2013.6927C8.15247%2013.5731%208.1031%2013.4591%208.03672%2013.355Z%22%20fill%3D%22%23FF1A1A%22/%3E%3Cpath%20d%3D%22M15.2662%2015.0581C15.1325%2014.9115%2014.943%2014.7654%2014.6443%2014.5847C14.6012%2014.5606%2014.5507%2014.5285%2014.504%2014.5006C14.2553%2014.351%2013.9556%2014.1936%2013.6057%2014.0035C13.068%2013.7083%2012.5299%2013.4206%2011.9868%2013.1352L11.0272%2012.6262C10.9774%2012.6116%2010.9269%2012.5757%2010.88%2012.5531C10.6957%2012.4649%2010.5002%2012.3827%2010.2973%2012.3449C10.2274%2012.3315%2010.1564%2012.3241%2010.0853%2012.3229C10.0393%2012.3223%209.9935%2012.3251%209.94796%2012.3312C9.75609%2012.361%209.57846%2012.4505%209.44039%2012.5872C9.30232%2012.7239%209.21087%2012.9006%209.17903%2013.0923C9.16442%2013.2142%209.16887%2013.3376%209.19228%2013.4581C9.23918%2013.7135%209.35321%2013.9661%209.47114%2014.1873L9.98361%2015.1478C10.2687%2015.6895%2010.5573%2016.228%2010.8532%2016.7649C11.044%2017.1151%2011.2029%2017.4148%2011.3517%2017.6632C11.3799%2017.7099%2011.4118%2017.7602%2011.4359%2017.8035C11.617%2018.1021%2011.7625%2018.2901%2011.91%2018.4249C12.0056%2018.5172%2012.1201%2018.5874%2012.2456%2018.6307C12.3775%2018.6745%2012.5172%2018.69%2012.6555%2018.6763C12.7259%2018.6679%2012.7957%2018.6559%2012.8648%2018.64C12.9328%2018.6218%2012.9998%2018.6%2013.0655%2018.5747C13.1365%2018.548%2013.2055%2018.5162%2013.2718%2018.4794C13.6614%2018.261%2014.0205%2017.9921%2014.3399%2017.6797C14.7229%2017.3028%2015.0615%2016.8918%2015.3249%2016.4213C15.3615%2016.3547%2015.3932%2016.2856%2015.4198%2016.2144C15.4446%2016.1486%2015.4661%2016.0815%2015.4842%2016.0135C15.4997%2015.9444%2015.5117%2015.8744%2015.5199%2015.8041C15.5331%2015.6659%2015.517%2015.5265%2015.4728%2015.3949C15.4294%2015.2689%2015.3588%2015.1538%2015.2662%2015.0581Z%22%20fill%3D%22%23FF1A1A%22/%3E%3C/svg%3E',
		mergeTagContext: 'social-yelp-link',
		data: {
			name: 'yelp',
			href: '#',
			target: '_blank',
			src: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M5.23438%2012.6189L6.15531%2012.4061C6.17468%2012.4016%206.20667%2012.3943%206.24575%2012.3817C6.49986%2012.3133%206.71761%2012.149%206.85314%2011.9232C6.98869%2011.6975%207.03144%2011.4279%206.9724%2011.1715C6.97111%2011.1658%206.96989%2011.1605%206.96859%2011.1549C6.93834%2011.0319%206.88535%2010.9157%206.81236%2010.8124C6.70915%2010.6814%206.58143%2010.5719%206.43635%2010.4899C6.26556%2010.3925%206.08681%2010.3096%205.90201%2010.2424L4.89226%209.87383C4.32559%209.66318%203.75896%209.45779%203.187%209.25749C2.81608%209.12549%202.50182%209.00964%202.22875%208.92518C2.1772%208.90931%202.12035%208.89343%202.07411%208.87746C1.74399%208.77629%201.51204%208.73434%201.31575%208.73296C1.18464%208.72814%201.05404%208.75196%200.933023%208.80275C0.806914%208.85714%200.693652%208.93752%200.600647%209.03867C0.554384%209.09129%200.510887%209.14626%200.470334%209.20333C0.43228%209.26157%200.397381%209.32171%200.365789%209.38367C0.331002%209.45017%200.301105%209.51903%200.276342%209.58979C0.0889311%2010.1438%20-0.00442136%2010.7252%200.000160845%2011.31C0.00353922%2011.8386%200.0177376%2012.5171%200.308305%2012.977C0.378321%2013.0951%200.472281%2013.1971%200.584127%2013.2766C0.791129%2013.4195%201.00031%2013.4384%201.21793%2013.454C1.54315%2013.4774%201.85822%2013.3974%202.17231%2013.3249L5.23181%2012.618L5.23438%2012.6189Z%22%20fill%3D%22%23FF1A1A%22/%3E%3Cpath%20d%3D%22M15.5088%207.7295C15.2574%207.20179%2014.9194%206.72002%2014.5088%206.3042C14.4557%206.25142%2014.3988%206.20257%2014.3386%206.15808C14.283%206.11635%2014.2252%206.07757%2014.1655%206.04189C14.1042%206.00824%2014.041%205.97791%2013.9764%205.951C13.8487%205.90087%2013.7118%205.87827%2013.5748%205.88467C13.4437%205.89212%2013.3158%205.92803%2013.2%205.98991C13.0243%206.07722%2012.8343%206.21755%2012.5815%206.45253C12.5466%206.48721%2012.5027%206.52635%2012.4633%206.56325C12.2548%206.75932%2012.0228%207.00104%2011.7467%207.28253C11.3203%207.7135%2010.9002%208.14684%2010.4828%208.58491L9.73604%209.35936C9.59933%209.50088%209.47477%209.65374%209.36382%209.81631C9.26929%209.95366%209.20234%2010.1082%209.16687%2010.2712C9.14637%2010.3961%209.14936%2010.5238%209.17576%2010.6477C9.17703%2010.6533%209.1783%2010.6586%209.17957%2010.6643C9.23863%2010.9208%209.39476%2011.1443%209.6153%2011.2878C9.83574%2011.4313%2010.1033%2011.4835%2010.3616%2011.4335C10.4026%2011.4276%2010.4343%2011.4205%2010.4536%2011.4158L14.4355%2010.4958C14.7493%2010.4232%2015.0677%2010.3568%2015.3496%2010.1932C15.5387%2010.0835%2015.7186%209.9748%2015.842%209.75553C15.9078%209.63506%2015.9477%209.50206%2015.9591%209.36526C16.0201%208.82258%2015.7368%208.20628%2015.5088%207.7295Z%22%20fill%3D%22%23FF1A1A%22/%3E%3Cpath%20d%3D%22M8.38218%209.4039C8.67073%209.04139%208.67002%208.50113%208.69583%208.05941C8.78268%206.58357%208.87412%205.10754%208.94661%203.63089C8.97428%203.07158%209.03451%202.51985%209.00122%201.95629C8.97382%201.49137%208.97047%200.957582%208.67693%200.576045C8.15878%20-0.0967628%207.05391%20-0.0414661%206.30005%200.0628987C6.06912%200.0949533%205.83778%200.138051%205.60832%200.19314C5.37884%200.24823%205.15172%200.308397%204.92951%200.379562C4.20614%200.61651%203.1892%201.05145%203.017%201.88495C2.91992%202.35615%203.14996%202.83816%203.32842%203.26817C3.54473%203.78944%203.84019%204.25899%204.10948%204.74968C4.82142%206.0451%205.54658%207.33244%206.26969%208.62114C6.48561%209.00583%206.721%209.49308%207.13879%209.69239C7.16641%209.70446%207.19461%209.71516%207.2233%209.72432C7.41065%209.79527%207.61486%209.80896%207.81006%209.76361C7.82184%209.76088%207.83335%209.75825%207.84486%209.75553C8.0251%209.70654%208.1883%209.60857%208.31634%209.4724C8.33946%209.45072%208.36144%209.42785%208.38218%209.4039Z%22%20fill%3D%22%23FF1A1A%22/%3E%3Cpath%20d%3D%22M8.03672%2013.355C7.92409%2013.1966%207.76426%2013.0778%207.58007%2013.0158C7.39589%2012.9537%207.19678%2012.9515%207.01119%2013.0095C6.9676%2013.0239%206.92512%2013.0416%206.88408%2013.0622C6.82061%2013.0945%206.76006%2013.1323%206.70309%2013.1751C6.53714%2013.298%206.3974%2013.4578%206.27017%2013.618C6.23789%2013.6588%206.20843%2013.7132%206.17004%2013.7482L5.52957%2014.6292C5.16654%2015.1227%204.80853%2015.6175%204.45352%2016.1202C4.22189%2016.4447%204.02153%2016.7187%203.86326%2016.9612C3.83327%2017.0069%203.80212%2017.0579%203.77363%2017.0985C3.58395%2017.392%203.47652%2017.6062%203.42138%2017.7969C3.37996%2017.9238%203.36686%2018.058%203.38298%2018.1904C3.40061%2018.3283%203.44703%2018.4608%203.51923%2018.5796C3.55761%2018.6391%203.59898%2018.6967%203.64318%2018.752C3.68911%2018.8054%203.73789%2018.8561%203.7893%2018.9041C3.84421%2018.9564%203.90293%2019.0047%203.96499%2019.0484C4.40706%2019.3559%204.89101%2019.5769%205.40007%2019.7477C5.82368%2019.8883%206.26401%2019.9725%206.70967%2019.998C6.78549%2020.0019%206.86148%2020.0001%206.93707%2019.9931C7.00712%2019.987%207.07681%2019.9772%207.14585%2019.9638C7.21484%2019.9477%207.28292%2019.9278%207.3498%2019.9045C7.47995%2019.8558%207.59845%2019.7804%207.69766%2019.6831C7.79158%2019.589%207.86386%2019.4756%207.90946%2019.3507C7.98349%2019.1662%208.03218%2018.9318%208.0642%2018.584C8.06701%2018.5346%208.07406%2018.4752%208.07898%2018.4208C8.10424%2018.1322%208.11582%2017.7932%208.13439%2017.3946C8.16571%2016.7815%208.19028%2016.1712%208.2097%2015.5591C8.2097%2015.5591%208.25095%2014.4714%208.25082%2014.4707C8.26027%2014.2201%208.25254%2013.9423%208.183%2013.6927C8.15247%2013.5731%208.1031%2013.4591%208.03672%2013.355Z%22%20fill%3D%22%23FF1A1A%22/%3E%3Cpath%20d%3D%22M15.2662%2015.0581C15.1325%2014.9115%2014.943%2014.7654%2014.6443%2014.5847C14.6012%2014.5606%2014.5507%2014.5285%2014.504%2014.5006C14.2553%2014.351%2013.9556%2014.1936%2013.6057%2014.0035C13.068%2013.7083%2012.5299%2013.4206%2011.9868%2013.1352L11.0272%2012.6262C10.9774%2012.6116%2010.9269%2012.5757%2010.88%2012.5531C10.6957%2012.4649%2010.5002%2012.3827%2010.2973%2012.3449C10.2274%2012.3315%2010.1564%2012.3241%2010.0853%2012.3229C10.0393%2012.3223%209.9935%2012.3251%209.94796%2012.3312C9.75609%2012.361%209.57846%2012.4505%209.44039%2012.5872C9.30232%2012.7239%209.21087%2012.9006%209.17903%2013.0923C9.16442%2013.2142%209.16887%2013.3376%209.19228%2013.4581C9.23918%2013.7135%209.35321%2013.9661%209.47114%2014.1873L9.98361%2015.1478C10.2687%2015.6895%2010.5573%2016.228%2010.8532%2016.7649C11.044%2017.1151%2011.2029%2017.4148%2011.3517%2017.6632C11.3799%2017.7099%2011.4118%2017.7602%2011.4359%2017.8035C11.617%2018.1021%2011.7625%2018.2901%2011.91%2018.4249C12.0056%2018.5172%2012.1201%2018.5874%2012.2456%2018.6307C12.3775%2018.6745%2012.5172%2018.69%2012.6555%2018.6763C12.7259%2018.6679%2012.7957%2018.6559%2012.8648%2018.64C12.9328%2018.6218%2012.9998%2018.6%2013.0655%2018.5747C13.1365%2018.548%2013.2055%2018.5162%2013.2718%2018.4794C13.6614%2018.261%2014.0205%2017.9921%2014.3399%2017.6797C14.7229%2017.3028%2015.0615%2016.8918%2015.3249%2016.4213C15.3615%2016.3547%2015.3932%2016.2856%2015.4198%2016.2144C15.4446%2016.1486%2015.4661%2016.0815%2015.4842%2016.0135C15.4997%2015.9444%2015.5117%2015.8744%2015.5199%2015.8041C15.5331%2015.6659%2015.517%2015.5265%2015.4728%2015.3949C15.4294%2015.2689%2015.3588%2015.1538%2015.2662%2015.0581Z%22%20fill%3D%22%23FF1A1A%22/%3E%3C/svg%3E',
			content: '',
		},
		tooltip: SOCIAL_DUPLICATE_TOOLTIP,
	},
];

const legacySocialIconKeyBySrc: Record<string, SocialNetworkKey> = {
	'https://bay.cdn.bkat.io/www/smc/onboarding/instagram-icon.svg': 'instagram',
	'https://cdn-icons-png.flaticon.com/128/174/174857.png': 'linkedin',
	'https://cdn-icons-png.flaticon.com/128/3536/3536505.png': 'linkedin-business',
	'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2220%22%20height%3D%2220%22%20fill%3D%22none%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cpath%20fill%3D%22%236c757d%22%20d%3D%22M8%2015A7%207%200%201%200%208%201a7%207%200%200%200%200%2014m0%201A8%208%200%201%201%208%200a8%208%200%200%201%200%2016%22/%3E%3Cpath%20fill%3D%22%236c757d%22%20d%3D%22M4.5%208a12.5%2012.5%200%200%201%20.527-3.5h5.946A12.5%2012.5%200%200%201%2011.5%208c0%201.23-.181%202.404-.527%203.5H5.027A12.5%2012.5%200%200%201%204.5%208m1.017-4.5a11.5%2011.5%200%200%201%201.004-2.01C7.067.876%207.656.5%208%20.5c.344%200%20.933.376%201.479.99.333.375.679.86%201.004%201.51zM10.483%204.5a10.5%2010.5%200%200%200-.741-1.235C9.284%202.75%208.744%202.5%208%202.5c-.744%200-1.284.25-1.742.765-.229.257-.48.685-.741%201.235zM4.5%208c0%201.23.181%202.404.527%203.5h5.946c.346-1.096.527-2.27.527-3.5s-.181-2.404-.527-3.5H5.027A12.5%2012.5%200%200%200%204.5%208m1.017%204.5a11.5%2011.5%200%200%200%201.004%202.01c.546.614%201.135.99%201.479.99.344%200%20.933-.376%201.479-.99.333-.375.679-.86%201.004-1.51zM10.483%2011.5H5.517c.261.55.512.978.741%201.235.458.515.998.765%201.742.765.744%200%201.284-.25%201.742-.765.229-.257.48-.685.741-1.235M4.964%203.5a13.5%2013.5%200%200%200-.719%202.5h-2.01a7%207%200%200%201%202.729-2.5zm-.719%209a13.5%2013.5%200%200%200%20.719%202.5h-2.73a7%207%200%200%201-.718-2.5zm7.791%202.5a13.5%2013.5%200%200%200%20.719-2.5h2.01a7%207%200%200%201-2.729%202.5zm.719-11.5a13.5%2013.5%200%200%201-.719%202.5h2.01a7%207%200%200%200-2.729-2.5z%22/%3E%3C/svg%3E':
		'website',
};

const socialPlatformByKey = new Map(
	socialPlatformOptions.map(option => [option.key, option]),
);
const socialPlatformBySrc = new Map(
	socialPlatformOptions.map(option => [option.data.src, option]),
);

function normalizeSocialNetworkKey(rawName: string): SocialNetworkKey | undefined {
	const normalizedName = rawName.trim().toLowerCase() as
		| SocialNetworkKey
		| LegacySocialNetworkKey;

	if (normalizedName === 'twitter' || normalizedName === 'x') {
		return 'social_x';
	}

	if (socialPlatformByKey.has(normalizedName as SocialNetworkKey)) {
		return normalizedName as SocialNetworkKey;
	}

	return undefined;
}

function getPlatformForElement(item: ISocial['data']['value']['elements'][0]) {
	if (item.name) {
		const normalizedName = normalizeSocialNetworkKey(item.name);
		if (normalizedName) {
			return socialPlatformByKey.get(normalizedName);
		}
	}

	if (item.src && socialPlatformBySrc.has(item.src)) {
		return socialPlatformBySrc.get(item.src);
	}

	if (item.src && legacySocialIconKeyBySrc[item.src]) {
		return socialPlatformByKey.get(legacySocialIconKeyBySrc[item.src]);
	}

	return undefined;
}

export function Social() {
	const { focusIdx } = useFocusIdx();
	const { focusBlock } = useBlock();
	const value = focusBlock?.data.value as ISocial['data']['value'];

	// Mark already-selected platforms as disabled, except website which allows up to MAX_WEBSITE_LINKS
	const platformOptionsWithDisabled = useMemo(() => {
		const selectedPlatformCounts = (value?.elements || []).reduce(
			(counts, element) => {
				const platformKey = getPlatformForElement(element)?.key;

				if (!platformKey) {
					return counts;
				}

				counts[platformKey] = (counts[platformKey] || 0) + 1;
				return counts;
			},
			{} as Partial<Record<SocialNetworkKey, number>>,
		);

		return socialPlatformOptions.map(option => ({
			...option,
			tooltipPlacement: SOCIAL_TOOLTIP_PLACEMENT,
			disabled: Boolean(selectedPlatformCounts[option.key]),
		}));
	}, [value?.elements]);

	if (!value) return null;

	return (
		<CollapseWrapper
			isChevron
			iconPosition='right'
			defaultActiveKey={['0', '1', '2']}
			style={{ padding: 0 }}
		>
			<Collapse.Item
				contentStyle={{ padding: 0 }}
				name='0'
				header={t('Content Block Settings')}
			>
				<div className='p-3'>
					<RadioGroupField
						label={t('Layout')}
						name={`${focusIdx}.attributes.mode`}
						options={options}
					/>
					<EditGridTabField
						tabPosition='top'
						name={`${focusIdx}.data.value.elements`}
						label=''
						labelHidden
						platformOptions={platformOptionsWithDisabled}
						renderItem={(item, index) => (
							<SocialElement
								item={item}
								index={index}
							/>
						)}
					/>
				</div>
			</Collapse.Item>

			<Collapse.Item
				contentStyle={{ padding: 0 }}
				name='1'
				header={t('Dimension')}
			>
				<div className='p-3'>
					<InputWithUnitField
						label={`${t('Icon width')} (px)`}
						name={`${focusIdx}.attributes.icon-size`}
						config={pixelAdapter}
					/>
					<Padding />
					<Padding
						attributeName='inner-padding'
						title={t('Icon padding')}
					/>
					<Padding
						attributeName='text-padding'
						title={t('Text padding')}
					/>
				</div>
			</Collapse.Item>

			<Collapse.Item
				contentStyle={{ padding: 0 }}
				name='2'
				header={t('Styling')}
			>
				<div className='p-3'>
					{/* Color inputs in two-column layout */}
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<Color title={t('Text color')} />
						</Grid.Col>
						<Grid.Col span={12}>
							<ContainerBackgroundColor title={t('Background color')} />
						</Grid.Col>
					</Grid.Row>

					{/* Font Family - Full Width */}
					<FontFamily />

					{/* Typography inputs in two-column layout */}
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<FontSize />
						</Grid.Col>
						<Grid.Col span={12}>
							<LineHeight />
						</Grid.Col>
					</Grid.Row>

					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<FontWeight />
						</Grid.Col>
					</Grid.Row>

					{/* Radio button groups - Full Width */}
					<FontStyle />
					<TextDecoration />
					<Align />
				</div>
			</Collapse.Item>

			<Collapse.Item
				contentStyle={{ padding: 0 }}
				name='3'
				header={t('Extra')}
			>
				<div className='p-3'>
					<ClassName />
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}

function SocialElement({
	item,
	index,
}: {
	item: ISocial['data']['value']['elements'][0];
	index: number;
}) {
	const { focusIdx } = useFocusIdx();
	const { focusBlock } = useBlock();
	const platform = getPlatformForElement(item);
	const attributes = focusBlock?.attributes as ISocial['attributes'] | undefined;
	const iconSrc = platform?.data.src || item.src;

	return (
		<div className='px-2 py-3'>
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '32px minmax(0, 1fr)',
					gap: 12,
					alignItems: 'center',
					marginBottom: 12,
				}}
			>
				<img
					style={{ width: 32, height: 32, objectFit: 'contain' }}
					src={iconSrc}
				/>
				<TextField
					label={t('Text')}
					name={`${focusIdx}.data.value.elements.[${index}].content`}
					formItem={{ style: { marginBottom: 0 } }}
				/>
			</div>
			<SocialLinkField
				name={`${focusIdx}.data.value.elements.[${index}].href`}
				mergeTagContext={platform?.mergeTagContext}
			/>
			<SwitchField
				label={<span className='fw-normal'>{t('Open in a new tab')}</span>}
				name={`${focusIdx}.data.value.elements.[${index}].target`}
				aria-label={t('Open in a new tab')}
				size='small'
				config={socialTargetAdapter}
				formItem={{
					style: {
						display: 'flex',
						alignItems: 'center',
						marginBottom: '0px',
					},
					labelCol: {
						span: 0,
						style: { order: 2, paddingLeft: '12px' },
					},
					wrapperCol: {
						span: 0,
						style: { order: 1 },
					},
				}}
			/>
		</div>
	);
}

function SocialLinkField({
	name,
	mergeTagContext,
}: {
	name: string;
	mergeTagContext?: MergeTagContext;
}) {
	const fieldProps = useField(name);

	return (
		<>
			<Space className='my-2 w-100 d-flex justify-content-between align-items-center'>
				<span className='fw-bold text-capitalize'>{t('Link address')}</span>
				{mergeTagContext && (
					<MergeTags
						value={fieldProps.input.value || ''}
						onChange={fieldProps.input.onChange}
						context={mergeTagContext}
						children={
							<span className='dynamicFieldDropdown text-secondary cursor-pointer d-flex gap-2'>
								<span>{`{ }`}</span>
								<span className='dynamic-field'>{t('Dynamic Fields')}</span>
							</span>
						}
						className='h-100 rounded-end-1 rounded-0 border border-start-0 mergeTag'
					/>
				)}
			</Space>
			<div className='d-flex w-100 mb-2'>
				<TextField
					placeholder=''
					formItem={{
						className: 'm-0',
						labelCol: {
							style: {
								display: 'none',
							},
						},
					}}
					name={name}
				/>
			</div>
		</>
	);
}
